import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";

function parseDateTime(value) {
    if (!value) return null;
    const dt = DateTime.fromISO(String(value).replace(/\[.*\]/, ""));
    return dt.isValid ? dt : null;
}

// Enriches raw /api/messages rows with the question (Answer) id each row
// concerns, so a BotErrorLog entry (keyed by answerId) can be attached to
// the exact message it happened during:
//   - bot-sent question rows (isBot=1) carry the question text verbatim,
//     so they're matched against the conversation's own question list;
//   - student reply rows (isBot=0) already carry a direct FK to the
//     originating question via answerConnector.fromId.
export default function useMessagesTimeline(conversationProgressId, conversationId, outerUserId) {
    const [messages, setMessages] = useState();
    const [status, setStatus] = useState("loading");

    const load = useCallback(() => {
        if (!conversationProgressId || !conversationId) return;
        setStatus("loading");
        Promise.all([
            axios.get("/api/messages?conversationprogress=" + conversationProgressId, { withCredentials: true }),
            axios.get("/api/answers/?conversation=" + conversationId, { withCredentials: true }),
            outerUserId
                ? axios.get("/api/bot-errors/by-conversation/" + conversationId + "?outerUserId=" + outerUserId, { withCredentials: true })
                : Promise.resolve({ data: [] }),
        ])
            .then(([messagesRes, questionsRes, errorsRes]) => {
                const textToQuestionId = new Map((questionsRes.data || []).map((q) => [q.text, q.id]));
                const questionsById = new Map((questionsRes.data || []).map((q) => [q.id, q]));
                const errorsByQuestionId = {};
                for (const entry of errorsRes.data || []) {
                    if (entry.answerId == null) continue;
                    if (!errorsByQuestionId[entry.answerId]) errorsByQuestionId[entry.answerId] = [];
                    errorsByQuestionId[entry.answerId].push(entry);
                }

                const rawMessages = messagesRes.data || [];
                const questionTotal = rawMessages.filter((m) => m.isBot === 1).length;
                let questionsSeen = 0;

                // Errors are about the bot's send attempt, so the badge only
                // belongs on the bot-sent question row (isBot=1) - attaching
                // it to the student's later, successfully-received reply too
                // would misleadingly suggest something failed on their side.
                const enriched = rawMessages.map((message, index) => {
                    if (message.isBot !== 1) {
                        // Response time only makes sense right after the question
                        // it answers - a preceding bot row in the same sequence.
                        let responseSeconds;
                        const previous = rawMessages[index - 1];
                        if (previous && previous.isBot === 1) {
                            const prevTime = parseDateTime(previous.dateTime);
                            const curTime = parseDateTime(message.dateTime);
                            if (prevTime && curTime) {
                                responseSeconds = Math.max(0, Math.round(curTime.diff(prevTime, "seconds").seconds));
                            }
                        }
                        return { ...message, errors: undefined, responseSeconds };
                    }

                    questionsSeen += 1;
                    const questionId = textToQuestionId.get(message.text);
                    // undefined = couldn't resolve the question at all (leave the
                    // "proposed options" row out rather than guess). Otherwise: a
                    // connector with clasification.id===1 ("Text answer") isn't a
                    // clickable option the user was ever shown - it's a pattern the
                    // bot matches a freely-typed reply against (confirmed against
                    // real data: e.g. "What are you doing?" has 4 such connectors
                    // holding acceptable phrases like "nothing"/"learn english", or
                    // one with no text at all meaning "accept anything"). Those still
                    // get surfaced, just not styled like real multiple-choice buttons.
                    const rawOptions = questionId != null ? (questionsById.get(questionId)?.answerConnectorsFrom || []) : undefined;
                    const isManualEntry = rawOptions?.some((opt) => opt.clasification?.id === 1) ?? false;
                    return {
                        ...message,
                        errors: questionId != null ? errorsByQuestionId[questionId] : undefined,
                        questionIndex: questionsSeen,
                        questionTotal,
                        questionOptions: rawOptions,
                        isManualEntry,
                    };
                });

                setMessages(enriched);
                setStatus("done");
            })
            .catch((err) => {
                console.error(err);
                setStatus("error");
            });
    }, [conversationProgressId, conversationId, outerUserId]);

    useEffect(() => {
        load();
    }, [load]);

    return { messages, status, reload: load };
}
